import type { JSX } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { TestChoice } from '../../game/testPlay.ts';
import type { EditorTool, PropId } from '../../sim/map/index.ts';
import { Tooltip } from '../tooltip.tsx';
import { EditorIcon, PlayIcon, TOOL_ICONS } from './icons.tsx';
import { PlacePropRailButton } from './propPicker.tsx';
import { RAIL_GROUPS, toolDef } from './tools.ts';

const FLYOUT_GAP_PX = 8;
const FLYOUT_PAD_PX = 8;

function placeTestFlyout(trigger: DOMRect, panel: DOMRect): { left: number; top: number } {
  const maxLeft = window.innerWidth - panel.width - FLYOUT_PAD_PX;
  const maxTop = window.innerHeight - panel.height - FLYOUT_PAD_PX;

  return {
    left: Math.min(Math.max(FLYOUT_PAD_PX, trigger.right + FLYOUT_GAP_PX), Math.max(FLYOUT_PAD_PX, maxLeft)),
    top: Math.min(Math.max(FLYOUT_PAD_PX, trigger.top), Math.max(FLYOUT_PAD_PX, maxTop)),
  };
}

function TestPlayRailButton({
  playable,
  testBoats,
  onTestBoat,
  onRefreshTestBoats,
}: {
  playable: boolean;
  testBoats: readonly TestChoice[];
  onTestBoat: (id: string) => void;
  onRefreshTestBoats: () => void;
}): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);

  const close = (): void => {
    setOpen(false);
    setBox(null);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close();
      }
    };

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }

      close();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const trigger = buttonRef.current;
    const panel = panelRef.current;

    if (!trigger || !panel) {
      return;
    }

    const layout = (): void => {
      setBox(placeTestFlyout(trigger.getBoundingClientRect(), panel.getBoundingClientRect()));
    };

    layout();
    window.addEventListener('resize', layout);

    return () => {
      window.removeEventListener('resize', layout);
    };
  }, [open, testBoats]);

  const root = typeof document === 'undefined' ? null : document.getElementById('ui-root') ?? document.body;

  return (
    <>
      <Tooltip label="Test play" side="right" disabled={open}>
        <button
          ref={buttonRef}
          type="button"
          class="editor-rail-button"
          aria-label="Test play"
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : 'false'}
          disabled={!playable}
          onClick={() => {
            if (!playable) {
              return;
            }

            if (open) {
              close();
              return;
            }

            onRefreshTestBoats();
            setOpen(true);
          }}
        >
          <EditorIcon icon={PlayIcon} />
        </button>
      </Tooltip>
      {open && root
        ? createPortal(
            <div
              ref={panelRef}
              class="editor-prop-flyout ui-panel ui-chrome"
              role="menu"
              aria-label="Test play"
              style={{
                left: box ? `${box.left}px` : '-9999px',
                top: box ? `${box.top}px` : '0',
              }}
            >
              {testBoats.length === 0 ? (
                <button type="button" class="editor-prop-flyout-item" role="menuitem" disabled>
                  No boats
                </button>
              ) : (
                testBoats.map((boat) => (
                  <button
                    key={boat.id}
                    type="button"
                    class="editor-prop-flyout-item"
                    role="menuitem"
                    onClick={() => {
                      close();
                      onTestBoat(boat.id);
                    }}
                  >
                    {boat.label}
                  </button>
                ))
              )}
            </div>,
            root,
          )
        : null}
    </>
  );
}

export function EditorToolRail({
  tool,
  draftProp,
  playable,
  testBoats,
  canPlaceProps,
  canTransform,
  onSetTool,
  onSetDraftProp,
  onTestBoat,
  onRefreshTestBoats,
}: {
  tool: EditorTool;
  draftProp: PropId;
  playable: boolean;
  testBoats: readonly TestChoice[];
  canPlaceProps: boolean;
  canTransform: boolean;
  onSetTool: (tool: EditorTool) => void;
  onSetDraftProp: (prop: PropId) => void;
  onTestBoat: (id: string) => void;
  onRefreshTestBoats: () => void;
}): JSX.Element {
  return (
    <div class="editor-rail ui-panel ui-chrome" role="toolbar" aria-label="Tools">
      {RAIL_GROUPS.map((group, index) => (
        <div key={group.join('-')} class={`editor-rail-group${index > 0 ? ' is-divided' : ''}`}>
          {group.map((id) => {
            if (id === 'place-prop') {
              return (
                <PlacePropRailButton
                  key={id}
                  tool={tool}
                  draftProp={draftProp}
                  canPlaceProps={canPlaceProps}
                  onSetTool={onSetTool}
                  onSetDraftProp={onSetDraftProp}
                />
              );
            }

            const def = toolDef(id);
            const locked = (id === 'scale' || id === 'rotate') && !canTransform;
            return (
              <Tooltip
                key={id}
                label={locked ? `${def.label} — turn Elevation off` : def.label}
                shortcut={locked ? undefined : def.shortcut}
                side="right"
              >
                <button
                  type="button"
                  class="editor-rail-button"
                  data-tool={id}
                  aria-label={def.label}
                  aria-pressed={tool === id ? 'true' : 'false'}
                  disabled={locked}
                  onClick={() => onSetTool(id)}
                >
                  <EditorIcon icon={TOOL_ICONS[id]} />
                </button>
              </Tooltip>
            );
          })}
        </div>
      ))}
      <div class="editor-rail-group is-divided">
        <TestPlayRailButton
          playable={playable}
          testBoats={testBoats}
          onTestBoat={onTestBoat}
          onRefreshTestBoats={onRefreshTestBoats}
        />
      </div>
    </div>
  );
}
